import React, { useEffect, useState } from "react";
import {
  Loader2,
  Activity,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import adminApi from "./adminApi";

// =========================================================
// LOGIN THROUGH LABEL
// =========================================================

const loginThroughLabel = (value) => {
  if (value === 1 || value === "1") return "Front End";
  if (value === 2 || value === "2") return "Admin End";

  return "—";
};

// =========================================================
// FORMAT DATE TIME
// Output: 2026-08-18 16:37:18
// =========================================================

const formatDateTime = (value) => {
  if (!value) return "—";

  // If backend already sends:
  // 2026-08-18 16:37:18
  // return it directly.
  if (
    typeof value === "string" &&
    /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)
  ) {
    return value;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  // The backend stores/inserts correct IST wall-clock time, but once it's
  // serialized to JSON as an ISO string (e.g. "2026-08-18T16:37:18.000Z"),
  // that "Z" wrongly labels it as UTC even though the digits are already
  // IST. Using getFullYear()/getHours()/etc. (local-time getters) would
  // make the browser convert it AGAIN into local time, double-shifting it
  // by the timezone offset (+5:30). Using the UTC getters instead reads
  // the raw digits exactly as they came from the database, with no
  // conversion applied.
  const year = date.getUTCFullYear();

  const month = String(
    date.getUTCMonth() + 1
  ).padStart(2, "0");

  const day = String(
    date.getUTCDate()
  ).padStart(2, "0");

  const hours = String(
    date.getUTCHours()
  ).padStart(2, "0");

  const minutes = String(
    date.getUTCMinutes()
  ).padStart(2, "0");

  const seconds = String(
    date.getUTCSeconds()
  ).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

// =========================================================
// COMPONENT
// =========================================================

export default function ManageActivityReport() {
  const [items, setItems] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  const [page, setPage] = useState(1);

  const [totalPages, setTotalPages] = useState(1);

  const [total, setTotal] = useState(0);

  const [limit, setLimit] = useState(100);

  // =========================================================
  // FETCH ACTIVITY REPORT
  // =========================================================

  const fetchItems = async (pageNo) => {
    setLoading(true);
    setError("");

    try {
      const res = await adminApi.get(
        "/admin/activity-report",
        {
          params: {
            page: pageNo,
          },
        }
      );

      setItems(
        res.data.activityReport || []
      );

      setTotalPages(
        res.data.totalPages || 1
      );

      setTotal(
        res.data.total || 0
      );

      setLimit(
        res.data.limit || 100
      );
    } catch (err) {
      console.error(
        "Could not load activity report:",
        err
      );

      setError(
        err.response?.data?.message ||
          "Could not load activity report."
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // LOAD PAGE
  // =========================================================

  useEffect(() => {
    fetchItems(page);
  }, [page]);

  // =========================================================
  // PAGE NAVIGATION
  // =========================================================

  const goToPage = (p) => {
    if (
      p < 1 ||
      p > totalPages ||
      p === page
    ) {
      return;
    }

    setPage(p);
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div className="h-full flex flex-col">

      {/* =====================================================
          TOOLBAR
      ===================================================== */}

      <div className="flex flex-wrap items-center justify-between gap-3 shrink-0 mb-2">

        <div>

          <h3 className="font-display font-bold text-[16px] bg-gradient-to-r from-[#0B2545] to-[#0EA5A4] bg-clip-text text-transparent tracking-tight">
            Manage Activity Report
          </h3>

        </div>

      </div>

      {/* =====================================================
          TABLE CARD
      ===================================================== */}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex-1 min-h-0 flex flex-col overflow-hidden">

        {/* ===================================================
            LOADING
        =================================================== */}

        {loading ? (

          <div className="flex items-center justify-center py-16 text-slate-400 text-sm gap-2">

            <Loader2
              size={18}
              className="animate-spin"
            />

            Loading activity report...

          </div>

        ) : error ? (

          /* =================================================
             ERROR
          ================================================= */

          <div className="py-16 text-center text-sm text-red-500">

            {error}

          </div>

        ) : items.length === 0 ? (

          /* =================================================
             EMPTY
          ================================================= */

          <div className="py-16 text-center">

            <Activity
              size={28}
              className="mx-auto text-slate-300 mb-2"
            />

            <p className="text-sm text-slate-400">
              No records available.
            </p>

          </div>

        ) : (

          /* =================================================
             TABLE
          ================================================= */

          <div className="flex-1 min-h-0 overflow-auto">

            <table className="w-full text-sm border-collapse">

              {/* =================================================
                  TABLE HEADER
              ================================================= */}

              <thead className="sticky top-0 z-10 shadow-md">

                <tr className="text-left text-sm">

                  <th className="th-colorful px-3 py-3 max-w-[240px] font-bold uppercase border-b-2 border-r">
                    Sl No.
                  </th>

                  <th className="th-colorful px-3 py-3 font-bold border-b-2 border-r">
                    IP
                  </th>

                  <th className="th-colorful px-3 py-3 font-bold border-b-2 border-r">
                    Session
                  </th>

                  <th className="th-colorful px-3 py-3 font-bold border-b-2 border-r">
                    User ID
                  </th>

                  <th className="th-colorful px-3 py-3 font-bold border-b-2 border-r">
                    URL
                  </th>

                  <th className="th-colorful px-3 py-3 font-bold border-b-2 border-r">
                    Created
                  </th>

                  <th className="th-colorful px-3 py-3 font-bold border-b-2">
                    Login Through
                  </th>

                </tr>

              </thead>

              {/* =================================================
                  TABLE BODY
              ================================================= */}

              <tbody>

                <AnimatePresence initial={false}>

                  {items.map((r, i) => (

                    <motion.tr
                      key={r.actrep_id}
                      layout
                      initial={{
                        opacity: 0,
                      }}
                      animate={{
                        opacity: 1,
                      }}
                      exit={{
                        opacity: 0,
                      }}
                      transition={{
                        duration: 0.2,
                        delay: i * 0.02,
                      }}
                      className="hover:bg-[#0EA5A4]/30 transition-colors duration-150 border-b border-slate-200"
                    >

                      {/* =========================================
                          SL NO.
                      ========================================= */}

                      <td className="px-3 py-2 text-slate-400 max-w-[40px] border-r border-slate-200">

                        {(page - 1) *
                          limit +
                          i +
                          1}

                      </td>

                      {/* =========================================
                          IP
                      ========================================= */}

                      <td className="px-3 py-2 text-slate-600 border-r border-slate-200">

                        {r.actrep_ip ||
                          "—"}

                      </td>

                      {/* =========================================
                          SESSION
                      ========================================= */}

                      <td
                        className="px-3 py-2 text-slate-500 max-w-[160px] truncate border-r border-slate-200"
                        title={
                          r.actrep_session ||
                          ""
                        }
                      >

                        {r.actrep_session ||
                          "—"}

                      </td>

                      {/* =========================================
                          USER ID
                      ========================================= */}

                      <td className="px-3 py-2 text-slate-600 max-w-[50px] border-r border-slate-200">

                        {r.actrep_userid ||
                          "—"}

                      </td>

                      {/* =========================================
                          URL
                      ========================================= */}

                      <td
                        className="px-3 py-2 text-slate-500 max-w-[240px] truncate border-r border-slate-200"
                        title={
                          r.actrep_url ||
                          ""
                        }
                      >

                        {r.actrep_url ||
                          "—"}

                      </td>

                      {/* =========================================
                          CREATED
                      ========================================= */}

                      <td className="px-3 py-2 text-slate-500 whitespace-nowrap border-r border-slate-200">

                        {formatDateTime(
                          r.actrep_created
                        )}

                      </td>

                      {/* =========================================
                          LOGIN THROUGH
                      ========================================= */}

                      <td className="px-3 py-2">

                        <span
                          className={`inline-flex items-center rounded-full text-xs px-2.5 py-1 ${
                            r.actrep_loginthrough ===
                              1 ||
                            r.actrep_loginthrough ===
                              "1"
                              ? "bg-sky-50 text-sky-600"
                              : "bg-[#0EA5A4]/10 text-[#0B7E7D]"
                          }`}
                        >

                          {loginThroughLabel(
                            r.actrep_loginthrough
                          )}

                        </span>

                      </td>

                    </motion.tr>

                  ))}

                </AnimatePresence>

              </tbody>

            </table>

          </div>

        )}

        {/* =====================================================
            PAGINATION
        ===================================================== */}

        {!loading &&
          !error &&
          totalPages > 1 && (

            <div className="shrink-0 flex items-center justify-between border-t border-slate-200 px-5 py-2">

              <span className="text-xs text-slate-400">

                Page {page} of{" "}
                {totalPages}

              </span>

              <div className="flex items-center gap-1.5">

                {/* FIRST */}

                <button
                  type="button"
                  onClick={() =>
                    goToPage(1)
                  }
                  disabled={page <= 1}
                  title="First page"
                  className="flex items-center justify-center w-7 h-7 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent transition"
                >

                  <ChevronsLeft
                    size={16}
                  />

                </button>

                {/* PREVIOUS */}

                <button
                  type="button"
                  onClick={() =>
                    goToPage(page - 1)
                  }
                  disabled={page <= 1}
                  title="Previous page"
                  className="flex items-center justify-center w-7 h-7 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent transition"
                >

                  <ChevronLeft
                    size={16}
                  />

                </button>

                {/* NEXT */}

                <button
                  type="button"
                  onClick={() =>
                    goToPage(page + 1)
                  }
                  disabled={
                    page >= totalPages
                  }
                  title="Next page"
                  className="flex items-center justify-center w-7 h-7 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent transition"
                >

                  <ChevronRight
                    size={16}
                  />

                </button>

                {/* LAST */}

                <button
                  type="button"
                  onClick={() =>
                    goToPage(
                      totalPages
                    )
                  }
                  disabled={
                    page >= totalPages
                  }
                  title="Last page"
                  className="flex items-center justify-center w-7 h-7 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent transition"
                >

                  <ChevronsRight
                    size={16}
                  />

                </button>

              </div>

            </div>

          )}

      </div>

    </div>
  );
}
